import React, { useEffect, useState, useRef } from 'react';
import api from '../../api';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { usePagination } from '../../hooks/usePagination';
import { GenericView, SearchInput } from '../../components/common/table';

const yearOptions = ['2024', '2025', '2026', '2027'];

const formatDateDisplay = (value) => {
  if (!value) return '-';
  const datePart = String(value).split('T')[0];
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return '-';
  return `${day}/${month}/${year}`;
};

const formatDateInput = (value) => {
  if (!value) return '';
  return String(value).split('T')[0];
};

const PublicHoliday = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  const pagination = usePagination(10);

  const [holidays, setHolidays] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    date: '',
    name: '',
    type: 'National',
    country: 'Indonesia',
  });
  const [errors, setErrors] = useState({});

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');

  const handleSearchInputChange = (value) => {
    setSearch(value);
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
    pagination.setPage(0);
  };

  const fetchHolidays = async (search = appliedSearchTerm, year = selectedYear) => {
    try {
      setIsLoading(true);
      const response = await api.get('/md/public-holidays');
      const data = response.data || [];
      const filtered = data.filter((item) => {
        const matchesSearch = search
          ? item.name?.toLowerCase().includes(search.toLowerCase())
          : true;
        const itemYear = String(item.date || '').split('T')[0].slice(0, 4);
        const matchesYear = year ? itemYear === year : true;
        return matchesSearch && matchesYear;
      });
      setHolidays(filtered);
      pagination.setTotalPages(Math.max(1, Math.ceil(filtered.length / pagination.size)));
      pagination.setTotal(filtered.length);
      pagination.setCurrentPageItems(
        Math.max(0, Math.min(pagination.size, filtered.length - pagination.page * pagination.size))
      );
    } catch (error) {
      let errorMessage = 'Failed to fetch public holidays.';
      let technicalDetails = '';
      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to view public holidays.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 500:
            errorMessage = 'Server encountered an error while fetching public holidays.';
            technicalDetails = error.response.data?.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while fetching public holidays.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }
      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
      setHolidays([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays(appliedSearchTerm, selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.size, appliedSearchTerm, selectedYear]);

  const handleYearChange = (e) => {
    const value = e.target.value;
    setSelectedYear(value);
    pagination.setPage(0);
  };

  const handleCreate = () => {
    setFormData({
      date: '',
      name: '',
      type: 'National',
      country: 'Indonesia',
    });
    setEditingId(null);
    setErrors({});
    const modal = new window.bootstrap.Modal(document.getElementById('modal-public-holiday'));
    modal.show();
  };

  const handleEdit = (item) => {
    setFormData({
      date: formatDateInput(item.date),
      name: item.name || '',
      type: item.type || 'National',
      country: item.country || 'Indonesia',
    });
    setEditingId(item.id);
    setErrors({});
    const modal = new window.bootstrap.Modal(document.getElementById('modal-public-holiday'));
    modal.show();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.name.trim()) newErrors.name = 'Holiday name is required';
    if (!formData.type.trim()) newErrors.type = 'Type is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const payload = {
        date: formData.date,
        name: formData.name.trim(),
        type: formData.type.trim(),
        country: formData.country.trim(),
      };

      if (editingId) {
        await api.put(`/md/public-holidays/${editingId}`, payload);
        setSuccessMessage('Public Holiday has been successfully updated.');
      } else {
        await api.post('/md/public-holidays', payload);
        setSuccessMessage('Public Holiday has been successfully added.');
      }

      setEditingId(null);
      setFormData({
        date: '',
        name: '',
        type: 'National',
        country: 'Indonesia',
      });
      setErrors({});
      await fetchHolidays(appliedSearchTerm, selectedYear);

      const modalEl = document.getElementById('modal-public-holiday');
      const bootstrapModal = window.bootstrap?.Modal.getInstance(modalEl);
      if (bootstrapModal) {
        bootstrapModal.hide();
        setTimeout(() => setShowSuccessModal(true), 300);
      } else {
        setShowSuccessModal(true);
      }
    } catch (error) {
      let errorMessage = 'Failed to save public holiday.';
      let technicalDetails = '';
      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid data provided.';
            technicalDetails = error.response.data?.message || 'The server rejected the request due to invalid data';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 409:
            errorMessage = 'A public holiday with the same date and name already exists.';
            technicalDetails = `Duplicate entry for ${formData.name} on ${formData.date}`;
            break;
          case 500:
            errorMessage = 'Server encountered an error while saving.';
            technicalDetails = error.response.data?.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while saving the public holiday.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }
      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteTargetId(id);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await api.delete(`/md/public-holidays/${deleteTargetId}`);
      setDeleteTargetId(null);
      setShowDeleteConfirmation(false);
      await fetchHolidays(appliedSearchTerm, selectedYear);
      setSuccessMessage('Public Holiday has been successfully deleted.');
      setShowSuccessModal(true);
    } catch (error) {
      let errorMessage = 'Failed to delete public holiday.';
      let technicalDetails = '';
      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to delete this item.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 404:
            errorMessage = 'Public holiday not found.';
            technicalDetails = 'The item may have already been deleted';
            break;
          case 500:
            errorMessage = 'Server error while deleting item.';
            technicalDetails = error.response.data?.message || 'Internal server error during deletion';
            break;
          default:
            errorMessage = 'An error occurred while deleting the public holiday.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred during deletion.';
        technicalDetails = error.message;
      }
      setShowDeleteConfirmation(false);
      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
    }
  };

  useEffect(() => {
    const modal = document.getElementById('modal-public-holiday');
    const handleHide = () => {
      setFormData({
        date: '',
        name: '',
        type: 'National',
        country: 'Indonesia',
      });
      setEditingId(null);
      setErrors({});
    };
    modal?.addEventListener('hidden.bs.modal', handleHide);
    return () => {
      modal?.removeEventListener('hidden.bs.modal', handleHide);
    };
  }, []);

  return (
    <>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">Public Holiday</h2>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button
                  className="btn btn-primary d-none d-sm-inline-block"
                  onClick={handleCreate}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="card">
            <div className="card-body">
              <GenericView
                data={holidays}
                columns={[
                  { key: 'date', label: 'Date', sortable: true, render: (value) => formatDateDisplay(value) },
                  { key: 'name', label: 'Holiday Name', sortable: true },
                  { key: 'type', label: 'Type', sortable: true, render: (value) => value || '-' },
                  { key: 'country', label: 'Country', sortable: true, render: (value) => value || '-' },
                ]}
                isLoading={isLoading}
                emptyStateMessage="No public holidays found"
                checkboxSelection={true}
                selectedRows={selectedIds}
                onSelectRow={(itemId, isSelected) => {
                  if (isSelected) {
                    setSelectedIds([...selectedIds, itemId]);
                  } else {
                    setSelectedIds(selectedIds.filter(id => id !== itemId));
                  }
                }}
                onSelectAllRows={(itemIds) => {
                  if (Array.isArray(itemIds) && itemIds.length > 0) {
                    setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                  } else {
                    setSelectedIds([]);
                  }
                }}
                onEdit={(item) => {
                  setSelectedIds([item.id]);
                  handleEdit(item);
                }}
                onDelete={(item) => {
                  setSelectedIds([item.id]);
                  handleDeleteClick(item.id);
                }}
                searchComponent={
                  <div className="d-flex gap-2">
                    <SearchInput
                      value={searchTerm}
                      onChange={handleSearchInputChange}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearchSubmit();
                        }
                      }}
                      placeholder="Search by holiday name..."
                      uniqueId="public-holiday-search"
                    />
                    <button type="button" className="btn btn-primary" onClick={handleSearchSubmit}>
                      Search
                    </button>
                    <select
                      className="form-select w-auto"
                      value={selectedYear}
                      onChange={handleYearChange}
                    >
                      <option value="">All Years</option>
                      {yearOptions.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                }
                actionButtons={
                  <div className="d-flex gap-2">
                    <div ref={actionsContainerRef} className="dropdown">
                      <a
                        ref={actionsToggleRef}
                        href="#"
                        className="btn dropdown-toggle"
                        data-bs-toggle="dropdown"
                        aria-expanded={isActionsOpen}
                        onClick={(e) => {
                          e.preventDefault();
                          const inst = actionsDropdownInstanceRef.current;
                          if (inst?.toggle) inst.toggle();
                          else setIsActionsOpen(prev => !prev);
                        }}
                      >Actions</a>
                      <div className="dropdown-menu">
                        <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); if (selectedIds.length === 1) { const item = holidays.find(h => h.id === selectedIds[0]); if (item) handleEdit(item); } }}
                          style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                          Edit
                        </a>
                        <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); if (selectedIds.length === 1) handleDeleteClick(selectedIds[0]); }}
                          style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                          Delete
                        </a>
                      </div>
                    </div>
                  </div>
                }
                pagination={{
                  currentPage: pagination.page + 1,
                  totalRows: pagination.total,
                  rowsPerPage: pagination.size,
                  onPageChange: (page) => pagination.setPage(page - 1),
                  onRowsPerPageChange: (size) => {
                    pagination.setSize(size);
                    pagination.setPage(0);
                  }
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                uniqueId="public-holiday-table"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal modal-blur fade"
        id="modal-public-holiday"
        tabIndex="-1"
        role="dialog"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {editingId ? 'Edit Public Holiday' : 'Add New Public Holiday'}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label required">Date</label>
                  <input
                    type="date"
                    className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                  />
                  {errors.date && (
                    <div className="invalid-feedback">{errors.date}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label required">Holiday Name</label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter holiday name"
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label required">Type</label>
                  <select
                    className={`form-select ${errors.type ? 'is-invalid' : ''}`}
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                  >
                    <option value="National">National</option>
                    <option value="Regional">Regional</option>
                  </select>
                  {errors.type && (
                    <div className="invalid-feedback">{errors.type}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label required">Country</label>
                  <input
                    type="text"
                    className={`form-control ${errors.country ? 'is-invalid' : ''}`}
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Enter country"
                  />
                  {errors.country && (
                    <div className="invalid-feedback">{errors.country}</div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary btn-3"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary ms-auto">
                  {editingId ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setDeleteTargetId(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this public holiday? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
      />

      <FailModal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="Operation Failed"
        message={failMessage}
        buttonText="Close"
      />
    </>
  );
};

export default PublicHoliday;
